import React from 'react'
// import './../../assets/privacyPolicy.css'

const PrivacyPolicy = () => {
 return (
  // Outer container with responsive max-width, center alignment, padding, and default font
  <div className='max-w-4xl mx-auto p-6 md:p-10 bg-white shadow-lg rounded-lg font-sans'>
   {/* Policy Title */}
   <h1 className='text-lg font-bold text-center border-b-2 border-gray-200 pb-4 mb-6'>
    PRIVACY POLICY
   </h1>
   <p className='text-center italic text-gray-500 mb-8'>
    Last updated: 24 October 2025
   </p>

   {/* Introduction */}
   <p className='mb-4'>
    This application/website (hereinafter "
    <strong>FarmRise One</strong>") is owned and operated by{' '}
    <strong>Bayer CropScience Limited India</strong>{' '}
    (hereinafter "<strong>us</strong>", "<strong>we</strong>
    ", or "<strong>Bayer</strong>").
   </p>

   <p className='mb-4'>
    At Bayer, we take data privacy seriously. We invite you
    to read the following carefully, as it explains to you
    how we collect, process, use, share and protect your
    personal data. Further, this document aims to inform you
    about your rights and how you may contact us with regard
    to your personal data privacy.
   </p>

   <p className='mb-8'>
    By using FarmRise One, you confirm that you have read
    and agree to be bound by this Privacy Policy and consent
    to the processing activities described under this
    Privacy Policy.
   </p>

   {/* 1. PERSONAL DATA THAT WE COLLECT */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    1. PERSONAL DATA THAT WE COLLECT
   </h2>
   <p className='mb-4'>
    We collect different types of personal data about you
    depending on the service FarmRise One provides you with.
    This includes:
   </p>
   <ol className='list-decimal list-inside ml-4 space-y-2 mb-6'>
    <li>
     <strong>Identity and Profile Data</strong>, such as
     your name, username, gender, photograph, field
     location, and information about your farming
     operations.
    </li>
    <li>
     <strong>Contact Data</strong>, such as your mobile
     number and e-mail address.
    </li>
    <li>
     <strong>Transaction Data</strong>, which includes
     details of transactions that may occur through FarmRise
     One or in connection with the services provided in
     FarmRise One, such as your booking or history.
    </li>
    <li>
     <strong>Technical Data</strong>, which includes your IP
     address, location data, details of the operating
     system, date and time stamps, and other technology on
     the devices that you use to access FarmRise One.
    </li>
    <li>
     <strong>Usage Data</strong>, which includes information
     about how you use FarmRise One such as your activity,
     access time and frequency of use of FarmRise One.
    </li>
    <li className='mb-8'>
     We also collect, use, and share aggregated data such as
     statistical or demographic data. Aggregated data could
     be derived from your personal data, but is not
     considered personal data under applicable laws as it
     does not directly or indirectly reveal your identity.
     However, if we combine or connect aggregated data with
     your personal data so that it can directly or
     indirectly identify you, we treat the combined data as
     personal data, which will be used in accordance with
     this Privacy Policy.
    </li>
   </ol>

   {/* 2. HOW DO WE COLLECT PERSONAL DATA? */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    2. HOW DO WE COLLECT PERSONAL DATA?
   </h2>
   <p className='mb-4'>
    We use different methods to collect personal data from
    and about you, through:
   </p>
   <ol className='list-decimal list-inside ml-4 space-y-4 mb-8'>
    <li>
     <strong>Direct Interactions</strong>: You provide us
     with your personal data when you interact with us. This
     also occurs when you authorise an FPO, PACS, BFLC or
     your authorised representative to share personal data
     with us. This includes personal data to:
     <ol className='list-disc list-inside ml-6 mt-1 space-y-1'>
      <li>create(s) an Account with us;</li>
      <li>
       use(s) FarmRise One or carry out other activities in
       connection with FarmRise One;
      </li>
      <li>
       request(s) marketing communications to be sent to
       you; or
      </li>
      <li>
       report(s) a problem with FarmRise One and/or, give(s)
       us feedback, or contact(s) us.
      </li>
     </ol>
    </li>
    <li>
     <strong>Automated Technologies or Interactions</strong>
     : Each time you visit FarmRise One or use its services,
     we will automatically collect Technical Data about your
     equipment, browsing actions, and patterns.
    </li>
    <li>
     <strong>
      Information we receive from other sources, including
      third parties and publicly available sources
     </strong>
     : We will receive personal data about you from various
     third parties and public sources for advertising and
     user analytics purposes.
    </li>
   </ol>

   {/* 3. HOW DO WE USE YOUR PERSONAL DATA? */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    3. HOW DO WE USE YOUR PERSONAL DATA?
   </h2>
   <p className='mb-4'>
    We will use your personal data where we need to provide
    you with the services on FarmRise One or where we need
    to comply with a legal obligation. We will use your
    personal data for the following purposes:
   </p>
   <ol className='list-decimal list-inside ml-4 space-y-2 mb-6'>
    <li>
     To verify your identity to register you as a user, and
     create your user account with us on FarmRise One;
    </li>
    <li>
     To use, reproduce, modify, adapt, publish, translate,
     create derivative works from, distribute, and display
     such personal data throughout the world in any media;
    </li>
    <li>
     To provide FarmRise One products and services and
     design new and adjusted services;
    </li>
    <li>
     To monitor trends and personalise your experience on
     FarmRise One;
    </li>
    <li>
     To improve the functionality of FarmRise One based on
     the information and feedback we receive from you;
    </li>
    <li>
     To improve customer service to effectively respond to
     your FarmRise One service requests and support needs;
    </li>
    <li>
     To track transactions and process payments on FarmRise
     One;
    </li>
    <li>
     To send periodic notifications to manage our
     relationship with you, including, to notify you of
     changes to FarmRise One services, send you information
     and updates pertaining to FarmRise One services you
     have availed, and to receive occasional news and
     updates related to us or FarmRise One services;
    </li>
    <li>
     To market and advertise FarmRise One services to you;
    </li>
    <li>
     To administer and protect FarmRise One services,
     including for troubleshooting, data analysis, system
     testing, and performing internal operations;
    </li>
    <li>To detect and prevent fraud;</li>
    <li>To comply with applicable laws and regulations;</li>
    <li>
     To respond to court orders, establish or exercise our
     legal rights, or defend ourselves against legal claims;
    </li>
    <li>
     To perform our obligations that arise out of agreements
     that we are about to enter or have entered into with
     you;
    </li>
    <li>
     To store your personal data with IT cloud solution
     providers; and
    </li>
    <li>To enforce our Terms.</li>
   </ol>
   <p className='mb-4'>
    You agree and acknowledge that by creating an account on
    FarmRise One, you authorise us, our associate partners,
    and affiliates to contact you via email or phone to
    provide FarmRise One services to you, ensure that you
    are aware of all the features of such services, and for
    related purposes.
   </p>
   <p className='mb-4'>
    You agree and acknowledge that all information
    pertaining to you, whether or not you directly provide
    it to us, including but not limited to personal
    correspondence such as emails, instructions from you,
    etc., may be collected, compiled, and shared by us in
    order to render FarmRise One services to you. This may
    include, but not be limited to, financial institutions,
    third-party service providers, third-party partners,
    storage providers, data analytics providers,
    consultants, lawyers, and auditors. We may also share
    this information with other entities in the Bayer group
    in connection with the above-mentioned purposes.
   </p>
   <p className='mb-4'>
    You agree and acknowledge that we may share data without
    your consent, when it is required by law or by any court
    or government agency or authority to disclose such
    information. Such disclosures are made in good faith and
    belief that it is reasonably necessary to do so for
    enforcing this Privacy Policy or the Terms, or in order
    to comply with any applicable laws and regulations.
   </p>
   <p className='mb-8'>
    If you fail to provide us with certain personal data as
    and when requested by us, we will not be able to provide
    you with all or certain features of FarmRise One.
   </p>

   {/* 4. THIRD PARTY LINKS AND USER EXPERIENCE IMPROVEMENT SERVICES */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    4. THIRD PARTY LINKS AND USER EXPERIENCE IMPROVEMENT
    SERVICES
   </h2>
   <ol className='list-decimal list-inside ml-4 space-y-4 mb-8'>
    <li>
     FarmRise One may, from time to time, contain
     third-party services or links to third-party services.
     Please note that these third-party services have their
     own privacy policies. We do not accept any
     responsibility or liability for the policies or for any
     personal data that may be collected through the
     third-party services. Please check their policies
     before you submit any personal data to such websites or
     use the third-party services.
    </li>
    <li>
     We use third-party user experience improvement services
     (including but not limited to those provided by Google
     Inc., and/or its affiliates) and applications to better
     understand your behaviour in connection with the
     services.
    </li>
    <li>
     Your relationship with these third parties and their
     services and tools is independent of your relationship
     with us. These third parties may allow you to permit or
     restrict the information that is collected. It may be
     in your interest to individually restrict or enable
     such data collections.
    </li>
    <li>
     The place of processing information depends on each
     third-party service provider, and you may wish to check
     the privacy policy of each of the service providers to
     identify the data shared and its purpose. You will be
     subject to a third party's privacy policy if you opt in
     to receive communications from third parties. We will
     not be responsible for the privacy standards and
     practices of third parties.
    </li>
   </ol>

   {/* 5. YOUR RIGHTS IN RELATION TO YOUR PERSONAL DATA */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    5. YOUR RIGHTS IN RELATION TO YOUR PERSONAL DATA
   </h2>
   <ol className='list-decimal list-inside ml-4 space-y-4 mb-8'>
    <li>
     <u>Access and Updating your Personal Data</u>: All
     personal data that you provide us with should be
     accurate, up-to-date, and true. Should you wish to
     correct or update your personal data available with us,
     we shall make best efforts to provide you with the
     ability to access and correct inaccurate or deficient
     data, subject to any legal requirements.
    </li>
    <li>
     <u>
      Opting out of Marketing and Promotional Communications
     </u>
     : When we send you marketing and promotional content
     through email, we make best efforts to provide you with
     the ability to opt out of such communications by using
     the opt-out instructions provided in such emails. You
     understand and acknowledge that it may take us up to 10
     (Ten) business days to give effect to your opt out
     request. Please note that we may still send you
     administrative emails about your user account or any
     Services you have requested or received from us, from
     which you cannot opt out.
    </li>
    <li>
     <u>Request the erasure of your personal data</u>: You
     agree and acknowledge that your personal data will
     continue to be stored and retained by us as required by
     or permitted under applicable laws or regulatory
     requirements. However, you will be able to ask us to
     delete or remove personal data to the extent permitted
     under applicable laws. In some instances, we will be
     unable to facilitate requests for the deletion of your
     data, as we may be required to store data for longer
     periods of time to comply with applicable laws or in
     case you wish to continue using the services in
     FarmRise One.
    </li>
    <li>
     <u>Right to Nominate</u>: You have the right to
     nominate an individual, in case of your incapacity or
     death, to exercise any of your privacy rights under
     applicable data protection laws.
    </li>
    <li>
     <u>Grievance Redressal</u>: You have the right to reach
     out to us in respect to any act or omission on our part
     relating to the performance of our obligations in
     respect to your personal data, or the exercise of any
     of your above rights.
    </li>
   </ol>

   {/* 6. TRANSFERS OF YOUR PERSONAL DATA */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    6. TRANSFERS OF YOUR PERSONAL DATA
   </h2>
   <p className='mb-4'>
    With whom we may share your personal data depends on the
    nature of your business with us. We only share your
    personal data to those who have a business need for it.
    Some of those parties include:
   </p>
   <ol className='list-decimal list-inside ml-4 space-y-2 mb-4'>
    <li>
     Bayer Group, Bayer Group's Subsidiaries and Affiliates
    </li>
    <li>
     <strong className='font-medium'>
      Commissioned data processors
     </strong>
     : when necessary, we engage with professional service
     data processors to process your personal data on our
     behalf. They are bound by contract with us to protect
     your personal data.
    </li>
    <li>
     <strong className='font-medium'>
      Relevant authorities
     </strong>
     : if legally required or necessary for the purpose
     described above.
    </li>
    <li>
     <strong className='font-medium'>
      External lawyers
     </strong>
     : to support legal decisions and to pursue or defend
     against legal claims.
    </li>
    <li>
     <strong className='font-medium'>
      Bayer's prospective buyers
     </strong>
     : when our company is bought, acquired, merged with
     another company (aka Mergers & Acquisitions) any other
     type of corporate or asset transition involving a
     change of ownership or control concerning us or our
     services.
    </li>
    <li>IT Cloud Solution Providers.</li>
    <li>
     Bayer's strategic partners, including but not limited
     to Rabo Partnerships B.V.
    </li>
   </ol>
   <p className='mb-8'>
    Bayer is a multinational organization comprised of
    multiple legal entities, affiliates, and subsidiaries
    around the world. In the course of our business, we may
    transfer your personal data to countries that may not be
    regarded as providing the same level of protection for
    personal data as the jurisdiction you are based in. In
    case such transfers occur, Bayer shall ensure necessary
    safeguards to protect your personal data, such as by
    entering into necessary contractual clauses for the
    transfer, or ask for your explicit consent.
   </p>

   {/* 7. DATA SECURITY */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    7. DATA SECURITY
   </h2>
   <p className='mb-4'>
    We implement appropriate security measures to protect
    your personal data from unauthorised access and
    disclosure, and follow standards prescribed by
    applicable law.
   </p>
   <p className='mb-4'>
    To enhance user convenience, FarmRise One allows you to
    remain logged in for a limited period (time period) of
    activity. After (time period) of inactivity, you will be
    required to log in again to continue using FarmRise One.
    This is a security feature designed to reduce the risk
    of unauthorized access.
   </p>
   <p className='mb-4'>
    We strongly advise you to take appropriate measures to
    keep your devices secure, such as using device passwords
    or biometric locks and avoiding sharing your devices
    with others. If your device is lost, stolen, or you
    believe it has been compromised, please notify us
    immediately so we can help protect your account.
   </p>
   <p className='mb-8'>
    Please note that we are not responsible for any
    unauthorized access to your account resulting from the
    loss, theft, or misuse of your device.
   </p>

   {/* 8. DATA RETENTION */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    8. DATA RETENTION
   </h2>
   <ol className='list-decimal list-inside ml-4 space-y-4 mb-8'>
    <li>
     You agree and acknowledge that your personal data will
     continue to be stored and retained by us for as long as
     necessary to fulfil our stated purpose(s) and for a
     reasonable period as specified under applicable laws
     after the termination of your account on the App or
     access to the Services to comply with our legal rights
     and obligations.
    </li>
    <li>
     In some circumstances, we may aggregate your personal
     data (so that it can no longer be associated with you)
     for research or statistical purposes, in which case we
     may use this information indefinitely without further
     notice to you.
    </li>
   </ol>

   {/* 9. UPDATES TO THIS PRIVACY POLICY */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    9. UPDATES TO THIS PRIVACY POLICY
   </h2>
   <ol className='list-decimal list-inside ml-4 space-y-4 mb-8'>
    <li>
     We may occasionally update this Privacy Policy. If we
     make changes to this Privacy Policy, we will notify you
     and upload the revised Privacy Policy on FarmRise One.
     Unless otherwise required by applicable law, such
     updates will take effect after a notice period of 15
     (Fifteen) days from the date of notification. To the
     extent permitted under applicable law, by using
     FarmRise One after the expiry of such notice period,
     you consent to updates made to this Privacy Policy.
    </li>
    <li>
     We regularly review and update our Privacy Policy, and
     we request you to regularly review this Privacy Policy.
     It is important that the personal data we hold about
     you is accurate and current. You are required to let us
     know if your personal data changes during your
     relationship with us.
    </li>
   </ol>

   {/* 10. GRIEVANCE OFFICER */}
   <h2 className='text-lg font-semibold border-b border-gray-300 pb-2 mb-4'>
    10. GRIEVANCE OFFICER
   </h2>
   <p className='mb-2'>
    If you have any questions about this Privacy Policy, how
    we process or handle your personal data, or otherwise,
    you may reach out to us, with your queries, grievances,
    feedback, and comments by contacting our grievance
    officer whose details are provided below:
   </p>
   <p className='mb-2'>
    <strong className='font-medium'>Address</strong>: Bayer
    CropScience Limited Bayer House, Central Avenue,
    Hiranandani Estate, Thane West-400607
   </p>
   <p className='mb-2'>
    <strong className='font-medium'>Email Address</strong>:
    dpo_india@bayer.com
   </p>
   <p className='mb-4'>
    Please mention FarmRise One as subject in your
    communication.
   </p>
  </div>
 )
}

export default function PrivacyPolicyWrapper() {
 // for using different languages and changing the text in to other languages
 // const intl = useIntl()
 return (
  <>
   {/* <PageTitle
        breadcrumbs={[{title: 'sdf', path: 'login', isActive: false}]}
        description='Home'
      >
        Home Page
      </PageTitle> */}
   <PrivacyPolicy />
  </>
 )
}
